import { useCallback, useEffect, useState, createRef } from 'react'
import { useEffectOnce } from 'usehooks-ts'
import styled from 'styled-components'
import headerImage from './images/header.jpg'
import headerImageWide from './images/header-wide.jpg'
import headerVideo from './video/header.mp4'
import Authors from 'components/Authors'
import { TextWidth } from '@ta-interaktiv/react-disco-grid'

const Header = ({
  tenant,
  colorMode,
  titleText,
  leadText,
  caption,
  authors,
  publishDate,
}: {
  tenant?: object
  colorMode?: string
  titleText?: string
  leadText?: string
  caption?: string
  authors?: any
  publishDate?: string
}) => {
  const [hasHeader, setHasHeader] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [isWide, setIsWide] = useState(false)

  // prevent jump to iframe load
  useEffect(() => {
    const interval = setInterval(() => {
      if (
        document.activeElement &&
        document.activeElement.tagName === 'IFRAME'
      ) {
        // console.log('scroll to top')
        // console.log('tagName', document.activeElement.tagName)
        window.scrollTo(0, 0)
      } else {
        // console.log('all OK')
      }
      clearInterval(interval)
    }, 1100)
    return () => clearInterval(interval)
  }, [])

  const handleResize = (): void => {
    setIsMobile(window.innerWidth < 711)
    setIsWide(
      window.innerWidth >= 1360 || window.innerWidth > window.innerHeight
    )
  }

  useEffectOnce(() => {
    const headerElement = document.querySelector('[class*=FullHeader_root]')
    if (headerElement) {
      setHasHeader(true)
    }
    // set height when window is resized
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  })

  return (
    <HeaderWrapper>
      <TopWrapper>
        {headerVideo && (
          <div className='header-image'>
            <video playsInline autoPlay loop muted>
              <source src={headerVideo} type='video/mp4' />
            </video>
          </div>
        )}
        {!headerVideo && (
          <div className='header-image'>
            <img src={isWide ? headerImageWide : headerImage} alt='header' />
          </div>
        )}
        <div className='title-wrapper'>
          <div className='titles'>
            <h1
              dangerouslySetInnerHTML={{
                __html: titleText ? titleText : 'titleText fehlt',
              }}
            ></h1>
            <h2>{leadText}</h2>
          </div>
        </div>
      </TopWrapper>
      {caption && <Caption>Video: Rick Ray/Videvo</Caption>}

      <div className='authors'>
        {authors && authors.length > 0 && (
          <Authors
            authors={authors}
            tenant={
              tenant
                ? tenant
                : {
                    key: 'tagesanzeiger',
                    name: 'Tages-Anzeiger',
                    domain: 'tagesanzeiger',
                    feedId: 3,
                    lang: 'de',
                  }
            }
          />
        )}
        {publishDate && <div className='publication'>{publishDate}</div>}
      </div>
    </HeaderWrapper>
  )
}

const HeaderWrapper = styled.div`
  .publication {
    font-size: 1.2rem;
    color: var(--gray-3);
    margin-bottom: 60px;
    text-align: center;
    z-index: 10;
    position: relative;
  }
`

const Caption = styled.div`
  font-size: 1.2rem;
  color: var(--meta-data);
  display: grid;
  position: relative;
  padding-left: 2rem;
  padding-right: 2rem;
  grid-column: 1;
  @media (min-width: 1931px) {
    margin-left: 20%;
  }
`

const TopWrapper = styled.div`
  background-color: transparent;
  overflow: hidden;
  position: relative;

  .header-image {
    background-color: #c8c8d1;
    position: relative;
    width: 100%;
    height: 70vh;
    min-height: 450px;
    @media (orientation: landscape) {
      height: 80vh;
      min-height: 600px;
    }
    object-fit: cover;
    img,
    video,
    div {
      width: 100%;
      height: 100%;
      position: absolute;
      object-fit: cover;
      overflow: visible;
      z-index: 1;
    }
  }

  .title-wrapper {
    z-index: 2;
    width: 668px;
    @media only screen and (max-width: 711px) {
      max-width: 80%;
    }
    position: absolute;
    top: 50%;
    left: 50%;
    @media only screen and (min-width: 0) {
      transform: translate(-50%, -100%);
    }
    @media only screen and (min-width: 768px) {
      transform: translate(-50%, -95%);
    }
    @media only screen and (min-width: 1200px) {
      transform: translate(-50%, -85%);
    }
    .titles {
      width: 100%;
      z-index: 99;
      top: 10vh;
      position: relative;
      @media only screen and (max-width: 711px) {
        top: 30vh;
      }
      @media (orientation: landscape) and (max-height: 1000px) {
        top: 18vh;
      }
      h1 {
        position: relative;
        margin: 0 auto;
        font-family: 'Suez One', serif;
        font-weight: 100;
        color: var(--brandblue-main);
        text-align: center;
        line-height: 0.6em;
        _text-shadow: 1px 1px 15px #000, 1px 1px 15px #000;
        @media only screen and (min-width: 0px) {
          font-size: 4rem;
          line-height: 4.5rem;
          _color: red;
        }
        @media only screen and (min-width: 375px) {
          font-size: 4.5rem;
          line-height: 5rem;
          _color: red;
        }
        @media only screen and (min-width: 768px) {
          font-size: 7rem;
          line-height: 8rem;
          _color: yellow;
        }
        @media only screen and (min-width: 1200px) {
          font-size: 9rem;
          line-height: 10rem;
          _color: orange;
          _text-shadow: 1px 1px 40px #000, 1px 1px 40px #000;
        }
        @media (orientation: landscape) and (max-height: 1000px) {
          _color: green;
          font-size: 7rem;
          line-height: 8rem;
        }
      }
      h2 {
        position: relative;
        left: -3.5%; // weird correction
        font-family: var(--font-sans-serif);
        color: var(--brandblue-main);
        padding: 1rem;
        font-weight: 300;
        font-size: 2.4rem;
        width: 100%;
        text-align: center;
        line-height: 1.3em;
        text-shadow: 1px 1px 8px rgba(255, 255, 255, 0.75);
        @media only screen and (max-width: 711px) {
          padding-top: 7vh;
          font-size: 2.2rem;
        }
      }
    }
  }
`

export default Header
